import { useAppNavigation } from '@euler/app/components/AppNavigationProvider';
import { Typography } from '@euler/app/components/typography';
import { useCurrentUser } from '@euler/app/flows/auth/context';
import { useSwitchAccount } from '@euler/app/flows/auth/hooks';
import { Avatar } from '@euler/components/Avatar';
import { Colors, StatusColors } from '@euler/components/Colors';
import { Icon } from '@euler/components/Icomoon';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useConfirm } from '@euler/functions/ux/useConfirm';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { PlatformUserApp } from '@euler/model/entity';
import { idEquals, sleep } from '@euler/utils';
import { usePersistFn } from '@euler/utils/hooks';
import { memo, useCallback, useState } from 'react';
import { FlatList, ListRenderItem, TouchableOpacity, View } from 'react-native';

const keyExtractor = (item: PlatformUserApp) => String(item.id);

export const SwitchAccountScreen = wrapNavigatorScreen(
  () => {
    const [savedUserSessions] = useState<PlatformUserApp[]>([]);
    const currentUser = useCurrentUser();
    const showOrgName = new Set(savedUserSessions.map(x => x.orgId)).size > 1;
    const navigation = useAppNavigation();
    const switchAccount = useSwitchAccount();
    const confirm = useConfirm();

    const onItemPress = usePersistFn(async (item: PlatformUserApp) => {
      if (!currentUser || idEquals(item.id, currentUser.app.id)) {
        return;
      }
      if (await confirm({ title: '是否切换至该账号? ' })) {
        navigation.popToTop();
        await sleep(350);
        await switchAccount(item.id);
      }
    });

    const renderItem: ListRenderItem<PlatformUserApp> = useCallback(
      ({ item }) => {
        return (
          <Cell
            item={item}
            showOrgName={showOrgName}
            isCurrent={
              currentUser != null && idEquals(item.id, currentUser.app.id)
            }
            onPress={onItemPress}
          />
        );
      },
      [showOrgName, currentUser, onItemPress],
    );
    return (
      <FlatList
        keyExtractor={keyExtractor}
        data={savedUserSessions}
        renderItem={renderItem}
        contentContainerStyle={{
          padding: 15,
        }}
      />
    );
  },
  {
    title: '切换账号',
  },
);

const Cell = memo(
  ({
    item,
    onPress,
    showOrgName,
    isCurrent,
  }: {
    item: PlatformUserApp;
    showOrgName?: boolean;
    isCurrent?: boolean;
    onPress?: (item: PlatformUserApp) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const handlePress = useCallback(() => {
      onPress?.(item);
    }, [item, onPress]);
    return (
      <TouchableOpacity
        onPress={handlePress}
        css={`
          background-color: #fff;
          margin-bottom: ${rpx(10)}px;
          padding: ${rpx(8)}px ${rpx(15)}px;
          border-radius: ${rpx(10)}px;
          flex-direction: row;
          justify-content: space-between;
          align-items: center;
          box-shadow: 0 0 ${rpx(10)}px rgba(0, 0, 0, 0.08);
        `}
      >
        <Avatar
          name={item.appName}
          textStyle={{ fontSize: rpx(24), lineHeight: rpx(44) }}
          css={`
            width: ${rpx(44)}px;
            height: ${rpx(44)}px;
            border-radius: ${rpx(44)}px;
            flex-shrink: 0;
            flex-grow: 0;
          `}
        />
        <View
          css={`
            flex: 1;
            padding: 0 ${rpx(10)}px;
          `}
        >
          <Typography.Label bold>{item.appName}</Typography.Label>
          {showOrgName ? (
            <Typography.Label medium size={rpx(14)} color="#444">
              {item.appTitle}
            </Typography.Label>
          ) : null}
          <Typography.Label regular size={rpx(12)} color="#666">
            {item.appName}
          </Typography.Label>
        </View>
        {isCurrent ? (
          <Icon
            name="checkmark-circle"
            color={StatusColors.Success}
            size={rpx(20)}
          />
        ) : null}
        <Icon
          name="chevron-thin-right"
          size={rpx(20)}
          color={Colors.Gray4}
          css={`
            margin-left: ${rpx(8)}px;
          `}
        />
      </TouchableOpacity>
    );
  },
);
